import * as React from 'react';
import propz from 'propz';
import { Component } from 'react';
import Lazy from 'lazy.js';
import { Field, Form, Formik } from 'formik';
import { Button } from '../Button/Button';
import { DateTimePickerField } from '../DateTimePickerField/DateTimePickerField';
import {
  ABSENCE_MESSAGE_INTERVAL,
  ABSENCE_MESSAGE_INTERVAL_SERVER_TO_CLIENT_MAPPING,
  ABSENCE_MESSAGE_REASON,
  ABSENCE_MESSAGE_REASON_SERVER_TO_CLIENT_MAPPING
} from 'Src/consts/absenceMessage';
import { SelectOption } from '../../helpers/table/table';
import { AppUser } from 'Src/views/App/App';
import { Role } from '../../models/roles';
import { ROLE } from '../../consts/user';
import * as Yup from 'yup';
import { getChildren } from '../../helpers/service/parent/children';
import { getSelectOptionsForSchools } from '../../helpers/table/select';
import { Loader } from '../Loader/Loader';
import './AbsenceMessageForm.scss';

interface Props {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  user: AppUser;
  availableRoles: Role[];
}

interface State {
  children: any[];
  schoolIds: any[];
  isLoading: boolean;
}

const AbsenceMessageSchema = Yup.object().shape({
  schoolId: Yup.string().required('Required'),
  userId: Yup.string().required('Required'),
  reasonAbsence: Yup.string().required('Required')
});

export class AbsenceMessageForm extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      children: [],
      schoolIds: [],
      isLoading: false
    };
  }

  componentDidMount() {
    const { availableRoles, user } = this.props;

    const schoolIds = Lazy(availableRoles)
      .filter(role => role.name === ROLE.PARENT)
      .map(role => role.permissions)
      .flatten()
      .filter(permission => {
        const isStudentIdExist = typeof permission.studentId !== 'undefined';
        const isAbsenceRequestEnabled = propz.get(permission, ['school', 'isAbsenceRequestEnabled'], false);

        return isStudentIdExist && isAbsenceRequestEnabled;
      })
      .map(permission => permission.school.id)
      .uniq()
      .toArray();

    this.setState({
      isLoading: true,
      schoolIds: schoolIds
    });

    getChildren(user).then(children => {
      this.setState({
        children: (children || []).filter(child => schoolIds.some(schoolId => String(child.schoolId) === String(schoolId))),
        isLoading: false
      });
    });
  }

  renderSchoolOptions(options: SelectOption[]): React.ReactNode {
    let result = options.map(option => {
      return (
        <option key={option.value} value={option.value}>
          {option.text}
        </option>
      );
    });

    //add empty option
    result.unshift(
      <option value={''} key={`absent_message_form_school_empty`}>
        Select school
      </option>
    );

    return result;
  }

  renderChildrenOptions(schoolId): React.ReactNode {
    const { children } = this.state;

    const childrenFiltered = children
      .filter(child => child.schoolId === schoolId)
      .map(child => ({
        text: `${child.firstName} ${child.lastName}`,
        value: child.id
      }));

    let result = childrenFiltered.map(option => (
      <option key={option.value} value={option.value}>
        {option.text}
      </option>
    ));

    //add empty option
    result.unshift(
      <option value={''} key={`absent_message_form_children_empty`}>
        Select child
      </option>
    );

    return result;
  }

  getPermissionIdByUserId(userId: string): string {
    const { children } = this.state;
    const child = children.find(child => child.id === userId);
    if (typeof child !== 'undefined') {
      return child.permissionId;
    } else {
      console.error(`Can not find child with id: ${userId}`);
      return '';
    }
  }

  render() {
    const { schoolIds, children, isLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    if (schoolIds.length === 0) {
      return (
        <>
          <p>
            Unfortunately, absence requests are not enabled on your account, please get in touch with your school
            directly to learn more.
          </p>
          <Button onClick={this.props.onCancel} text={'Close'} customClass={'mt-3 mb-3 mr-3 btn-secondary'} />
        </>
      );
    }

    const { availableRoles } = this.props;
    const isOneSchool = schoolIds.length === 1;
    const isOneChild = children.length === 1;

    const initialValues = {
      schoolId: isOneSchool ? schoolIds[0] : '',
      userId: isOneChild ? children[0].id : '',
      permissionId: isOneChild ? children[0].permissionId : '',
      details: '',
      dateAbsence: new Date(),
      intervalAbsence: ABSENCE_MESSAGE_INTERVAL.FULL,
      reasonAbsence: ''
    };

    const schoolsSelectOptions = getSelectOptionsForSchools(availableRoles)
      .filter(option => schoolIds.some(schoolId => String(schoolId) === String(option.value)));

    return (
      <div className="absenceRequestForm">
            <Formik
              initialValues={initialValues}
              validationSchema={AbsenceMessageSchema}
              onSubmit={values => {
                const { userId, permissionId, ...rest } = values;

                const dataToSubmit = {
                  ...rest,
                  playerDetails: {
                    userId: userId,
                    permissionId: permissionId
                  }
                };

                this.props.onSubmit(dataToSubmit);
              }}
              >
              {({ touched, errors, setFieldValue, values }) => (
                <Form>
                  <label>School</label>
                  <Field
                    disabled={isOneSchool}
                    component="select"
                    name="schoolId"
                    className="form-control mb-3"
                    onChange={event => {
                      const schoolId = event.target.value;
                      setFieldValue('schoolId', schoolId);
                      setFieldValue('userId', '');
                      setFieldValue('permissionId', '');
                    }}
                  >
                    {this.renderSchoolOptions(schoolsSelectOptions)}
                  </Field>
                  {touched.schoolId && errors.schoolId ? (
                    <div className="alert alert-danger">{String(errors.schoolId)}</div>
                  ) : null}

                  <label>Child</label>
                  <Field
                    disabled={!values.schoolId || isOneChild}
                    component="select"
                    name="userId"
                    className="form-control mb-3"
                    onChange={event => {
                      const userId = event.target.value;
                      const permissionId = this.getPermissionIdByUserId(userId);
                      setFieldValue('userId', userId);
                      setFieldValue('permissionId', permissionId);
                    }}
                  >
                    {this.renderChildrenOptions(values.schoolId)}
                  </Field>
                  {touched.userId && errors.userId ? <div className="alert alert-danger">{String(errors.userId)}</div> : null}

                  <label>Date</label>
                  <Field
                    name="dateAbsence"
                    >
                      {({ field }) => {
                        return (
                          <div className="mb-3">
                            <DateTimePickerField
                              value={field.value}
                              onChange={dateAbsence => {
                                setFieldValue('dateAbsence', dateAbsence);
                              }}
                              minDate={new Date()}
                              showTimeSelect={false}
                              popperPlacement="bottom-start"
                            />
                          </div>
                        );
                    }}
                  </Field>

                  <label>Interval</label>
                  <div className="absenceRequestForm__chips">
                    {[ABSENCE_MESSAGE_INTERVAL.FULL, ABSENCE_MESSAGE_INTERVAL.HALF].map(interval => (
                      <button
                        key={interval}
                        type="button"
                        className={values.intervalAbsence === interval ? 'is-selected' : ''}
                        onClick={() => setFieldValue('intervalAbsence', interval)}
                      >
                        {ABSENCE_MESSAGE_INTERVAL_SERVER_TO_CLIENT_MAPPING[interval]}
                      </button>
                    ))}
                  </div>

                  <label>Reason for absence</label>
                  <div className="absenceRequestForm__chips">
                    {[ABSENCE_MESSAGE_REASON.ILLNESS, ABSENCE_MESSAGE_REASON.MEDICAL_APPOINTMENT, ABSENCE_MESSAGE_REASON.OTHER_REASON].map(reason => (
                      <button
                        key={reason}
                        type="button"
                        className={values.reasonAbsence === reason ? 'is-selected' : ''}
                        onClick={() => setFieldValue('reasonAbsence', reason)}
                      >
                        {ABSENCE_MESSAGE_REASON_SERVER_TO_CLIENT_MAPPING[reason]}
                      </button>
                    ))}
                  </div>

                  {touched.reasonAbsence && errors.reasonAbsence ? (
                    <div className="alert alert-danger">{String(errors.reasonAbsence)}</div>
                  ) : null}

                  <label>Details</label>
                  <Field component="textarea" name="details" rows="3" className="form-control mb-3" />

                  <div className="absenceRequestForm__actions">
                  <button type="button" onClick={this.props.onCancel} className="parentButton">Cancel</button>
                  <button type="submit" className="parentButton parentButton--primary">
                    Submit
                  </button>
                  </div>
                </Form>
              )}
            </Formik>
      </div>
    );
  }
}


